/** Parse teacher name / matricule from natural-language queries. */

export function normalizeTeacherText(text) {
  return String(text ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

export function normalizeReg(text) {
  return normalizeTeacherText(text).replace(/[\s.-]/g, "");
}

/** Extract matricule / registration number if present in the query. */
export function parseRegistrationHint(text) {
  const raw = String(text ?? "").trim();
  const patterns = [
    /(?:registration\s*(?:no|number|#)?|reg(?:istration)?\s*(?:no|number|#)?|matricule|numero\s*d['']?enregistrement|n°?\s*matricule)\s*[:\s#-]*([A-Za-z0-9-]+)/i,
    /(?:matricule|registration|reg)\s*[:\s#-]+([A-Za-z0-9-]+)/i,
    /\b(?:no|number|n°)\s*[:\s#-]*(\d{3,}[A-Za-z0-9-]*)\b/i,
  ];
  for (const re of patterns) {
    const match = raw.match(re);
    if (match?.[1]) return match[1].trim();
  }
  const onlyNumber = raw.match(/^\d{3,}[A-Za-z0-9-]*$/);
  if (onlyNumber) return onlyNumber[0];
  return null;
}

const BOILERPLATE_PATTERNS = [
  /^(?:please|pls|can you|could you|would you|will you|i want to|i'd like to|i would like to|i need to|help me|help us|i am looking to|i'm looking to|looking to)\s+/gi,
  /^(?:know|learn|get|find out|tell me|show me|give me|see|check|lookup|look up)\s+(?:more\s+)?(?:about|info on|information on|details on|the details of|the profile of|the status of)\s+/gi,
  /^(?:s'il vous plait|svp|peux tu|peux-tu|pouvez vous|pouvez-vous|je veux|j'aimerais|aide moi|aidez moi|aidez-moi|aide-moi|je cherche a|je cherche à)\s+/gi,
  /^(?:connaitre|connaître|savoir|trouver|chercher|rechercher|montre|montrez|donne|donnez|parle moi de|parle-moi de|parlez moi de|parlez-moi de)\s+/gi,
];

const TEACHER_PHRASE_PATTERNS = [
  /(?:an?\s+)?(?:teacher|enseignant(?:e)?|prof(?:essor)?(?:e)?(?:ssor)?(?:e)?)\s+(?:named|called|nomme|nommé|nommee|nommée|with\s+(?:the\s+)?name)\s+/gi,
  /(?:about|for|on|de|du|de la|d[''])\s+(?:an?\s+)?(?:teacher|enseignant(?:e)?)\s+(?:named|called|nomme|nommé|nommee|nommée)?\s*/gi,
  /(?:teacher|enseignant(?:e)?)\s+(?:named|called|nomme|nommé|nommee|nommée)\s+/gi,
  /(?:find|search|look up|lookup|locate|trouver|chercher|rechercher)\s+(?:an?\s+)?(?:teacher|enseignant(?:e)?)\s*/gi,
  /(?:help(?:\s+me|\s+us)?\s+(?:to\s+)?(?:find|search|locate|look up|lookup|trouver|chercher|rechercher))\s+(?:an?\s+)?(?:teacher|enseignant(?:e)?)\s*/gi,
  /^(?:teacher|enseignant(?:e)?)\s*[:\-]?\s*/gi,
  /^(?:a|an|the|un|une|le|la|l')\s+(?:teacher|enseignant(?:e)?)\s*/gi,
];

const TRAILING_NOISE_PATTERNS = [
  /\s+(?:please|thanks|thank you|merci|svp|s'il vous plait|s'il vous plaît)\.?$/gi,
  /\s+(?:for me|for us|right now|today)\.?$/gi,
];

/** Strip conversational filler so "I want to know about a teacher named Hamza" → "Hamza". */
export function stripTeacherQueryBoilerplate(text) {
  let term = String(text ?? "").trim();
  if (!term) return "";

  for (let pass = 0; pass < 3; pass += 1) {
    BOILERPLATE_PATTERNS.forEach((re) => {
      term = term.replace(re, "");
    });
    TEACHER_PHRASE_PATTERNS.forEach((re) => {
      term = term.replace(re, "");
    });
    term = term.trim();
  }

  TRAILING_NOISE_PATTERNS.forEach((re) => {
    term = term.replace(re, "");
  });

  term = term.replace(/^(named|called|nomme|nommé|nommee|nommée|with|avec)\s+/i, "");
  term = term.replace(/^(matricule|registration|reg(?:istration)?)\s*[:\s#-]*/i, "");
  term = term.replace(/^(no|number|n°)\s+/i, "");
  term = term.replace(/[?.!]+$/, "").trim();

  const normalized = normalizeTeacherText(term);
  if (
    !normalized ||
    /^(help me|help us|aide moi|aidez moi|find|search|chercher|trouver|look up|lookup)$/.test(normalized) ||
    /^(?:a|an|the|un|une|le|la)?\s*(?:teacher|enseignant(?:e)?|prof(?:essor)?(?:e)?(?:ssor)?(?:e)?)$/.test(
      normalized
    )
  ) {
    return "";
  }

  return term;
}

/** True when the user wants to find/lookup a teacher (with or without a name). */
export function isTeacherLookupQuery(input) {
  const q = normalizeTeacherText(input);
  if (!q) return false;

  if (/\b(find|search|look up|lookup|locate|chercher|trouver|rechercher)\b/.test(q) && /\b(teacher|enseignant)\b/.test(q)) {
    return true;
  }
  if (/\b(know about|tell me about|learn about|info on|information on|who is|who's|qui est)\b/.test(q) && /\b(teacher|enseignant)\b/.test(q)) {
    return true;
  }
  if (/\b(teacher|enseignant)\b.*\b(named|called|nomme|nomme|matricule|registration)\b/.test(q)) {
    return true;
  }
  if (/\b(named|called|nomme|nomme)\b/.test(q) && /\b(teacher|enseignant)\b/.test(q)) {
    return true;
  }
  if (/\b(about|profil|profile|fiche|story|status of)\b.*\b(teacher|enseignant)\b/.test(q)) {
    return true;
  }
  return false;
}

/** User wants help finding a teacher but did not give a name/matricule. */
export function isTeacherSearchWithoutName(input) {
  const q = normalizeTeacherText(input);
  if (!q || !isTeacherLookupQuery(q)) return false;

  const term = stripTeacherQueryBoilerplate(input);
  if (!term || term.length < 2) return true;

  const normalizedTerm = normalizeTeacherText(term);
  if (
    /^(?:a|an|the|un|une|le|la)?\s*(?:teacher|enseignant(?:e)?|prof(?:essor)?(?:e)?(?:ssor)?(?:e)?)\s*$/.test(
      normalizedTerm
    )
  ) {
    return true;
  }
  if (/^(?:help|aide|find|search|chercher|trouver|look up|lookup|help me|aide moi)$/.test(normalizedTerm)) {
    return true;
  }
  return false;
}

/** Portal questions that start with "what/how…" but are NOT teacher follow-ups. */
const NON_TEACHER_FOLLOW_UP_PATTERNS = [
  /\b(what can you|what do you|what you can|what are you|what is this|what is the portal)\b/,
  /\b(que peux tu|que pouvez vous|que fais tu|que faites vous|quoi faire|tu fais quoi)\b/,
  /\b(capabilities|fonctionnalit|features|help me with|how to use|how does this|comment utiliser|comment ca marche)\b/,
  /\b(portal summary|resume du portail|résumé du portail|pending leave|conges en attente|congés en attente)\b/,
  /\b(how to change password|change password|changer mot de passe|how to create (?:a )?user|create (?:a )?user|how to change (?:the )?language)\b/,
  /\b(how many|combien|guide affectation|guide conge|guide congé|assignment guide|leave guide)\b/,
  /\b(open |go to |ouvrir |naviguer |aller a |aller à |take me to |show me the )\b/,
  /\b(license quota|quota licence|area attendance|synthese presence|synthèse présence)\b/,
];

/** Short follow-up about the last teacher discussed (license, leave, assignment…). */
export function isTeacherFollowUpQuery(input) {
  const q = normalizeTeacherText(input);
  if (!q || q.length > 120) return false;

  if (parseRegistrationHint(input)) return false;
  if (isTeacherLookupQuery(input) && !isTeacherSearchWithoutName(input)) return false;
  if (isTeacherNameFollowUp(input)) return false;
  if (NON_TEACHER_FOLLOW_UP_PATTERNS.some((re) => re.test(q))) return false;

  const followUpPatterns = [
    /^(?:what about|how about|and )(?:their|his|her|le|la|son|sa|leur|the)\b/,
    /^(?:is|are)\s+(?:he|she|they|on)\b/,
    /\b(is|are)\s+(?:he|she|they)\s+(?:on\s+)?(?:a\s+)?(?:leave|conge|congé|assigned|licensed|attending|present)\b/,
    /\b(est[- ]ce que|est ce que)\s+(?:il|elle|lui)\b/,
    /\bon\s+a\s+leave\b/,
    /\b(their|his|her|son|sa|leur)\s+(?:license|licence|leave|conge|congé|assignment|affectation|school|score|status|attendance|presence|présence)\b/,
    /\b(on leave|on a leave|assigned|licensed|attending|present|en conge|en congé|affect|licencie|licencié|presente|présente)\??$/,
    /^(?:same teacher|that teacher|this teacher|cet enseignant|cette enseignante|lui|elle|eux|them|they)\b/,
    /^(?:and|also)\s+(?:is|are|was|were|what|how)\s+(?:he|she|they|their|his|her)\b/,
    /^(?:tell me more|more info|more details|anything else)\s+(?:about\s+)?(?:him|her|them|this|that|the teacher|cette enseignante|cet enseignant)\b/,
    /^(?:license|licence|leave|conge|congé|assignment|affectation|attendance|presence|présence|score|status)\??$/,
  ];

  return followUpPatterns.some((re) => re.test(q));
}

/** User is answering with a teacher name after a search prompt ("his name is Hamza", "yes Dupont"). */
export function isTeacherNameFollowUp(input) {
  return Boolean(extractTeacherNameFollowUp(input));
}

export function extractTeacherNameFollowUp(input) {
  const raw = String(input ?? "").trim();
  if (!raw || raw.length > 80) return null;

  const patterns = [
    /^(?:yes|yeah|yep|yup|ok|okay|sure|right|correct|oui|d'accord|bien sur|bien sûr|exactement|voila|voilà)[,!.]?\s+(?:his|her|their|the|l')?\s*(?:name is|nom est|nom c'est|it's|its|it is|c'est|ce est)\s+(.+)$/i,
    /^(?:his|her|their|the|l')\s+name\s+is\s+(.+)$/i,
    /^(?:it's|its|it is|c'est|ce est|name is|nom est|nom c'est|called|named|nomme|nommé|nommee|nommée|appele|appelé|appelee|appelée)\s+(.+)$/i,
    /^(?:the teacher is|teacher is|l'enseignant est|l'enseignante est|enseignant est|enseignante est)\s+(.+)$/i,
  ];

  for (const re of patterns) {
    const match = raw.match(re);
    if (match?.[1]) {
      const term = stripTeacherQueryBoilerplate(match[1]);
      if (term && term.length >= 2) return term;
    }
  }

  const shortAffirm = raw.match(/^(?:yes|yeah|yep|yup|ok|okay|sure|oui|d'accord)\s+(.+)$/i);
  if (shortAffirm?.[1]) {
    const term = stripTeacherQueryBoilerplate(shortAffirm[1]);
    if (term && term.length >= 2) return term;
  }

  return null;
}

/** Resolve a teacher search term from a follow-up or bare name/matricule reply. */
export function resolveTeacherSearchReply(input, { awaitingName = false } = {}) {
  const raw = String(input ?? "").trim();
  if (!raw) return null;

  const fromFollowUp = extractTeacherNameFollowUp(raw);
  if (fromFollowUp) return fromFollowUp;

  const regHint = parseRegistrationHint(raw);
  if (regHint) return regHint;

  if (!awaitingName) return null;

  const cleaned = stripTeacherQueryBoilerplate(raw);
  if (cleaned && cleaned.length >= 2) return cleaned;

  if (/^[a-zA-ZÀ-ÿ0-9][\wÀ-ÿ.-]{0,40}$/.test(raw)) return raw;

  return null;
}

/** Expand a follow-up using the last teacher from conversation context. */
export function expandTeacherFollowUp(input, lastTeacher, language) {
  if (!lastTeacher?.name && !lastTeacher?.reg && !lastTeacher?.id) return input;

  const label = lastTeacher.reg || lastTeacher.name;
  if (!label) return input;

  const q = normalizeTeacherText(input);
  const en = language === "en";

  if (/^(?:license|licence)\??$/.test(q)) {
    return en
      ? `Tell me about teacher ${label} license status`
      : `Licence de l'enseignant ${label}`;
  }
  if (/^(?:leave|conge|congé)\??$/.test(q)) {
    return en ? `Is teacher ${label} on leave?` : `Est-ce que ${label} est en congé ?`;
  }
  if (/^(?:assignment|affectation)\??$/.test(q)) {
    return en ? `Is teacher ${label} assigned?` : `Est-ce que ${label} est affectée ?`;
  }
  if (/^(?:attendance|presence|présence)\??$/.test(q)) {
    return en ? `Teacher ${label} attendance` : `Présence enseignant ${label}`;
  }
  if (/\b(on leave|on a leave|en conge|en congé)\b/.test(q) || /^is (?:he|she|they)/.test(q)) {
    return en ? `Is teacher ${label} on leave?` : `Est-ce que ${label} est en congé ?`;
  }
  if (/\b(assigned|affect|affectee|affectée)\b/.test(q)) {
    return en ? `Is teacher ${label} assigned?` : `Est-ce que ${label} est affectée ?`;
  }
  if (/\b(license|licence|licensed|licencie|licencié)\b/.test(q)) {
    return en ? `Tell me about teacher ${label} license status` : `Licence de l'enseignant ${label}`;
  }

  return en ? `Tell me about teacher ${label}` : `Fiche enseignant ${label}`;
}

export function extractTeacherStoryTerm(input, language) {
  const raw = String(input ?? "").trim();
  const regHint = parseRegistrationHint(raw);
  if (regHint) return regHint;

  const patterns = [
    /(?:tell me about|status of|story for|profile of|info on|overview of|show(?:\s+me)?|know about|learn about|information on|details on)\s+(?:an?\s+)?teacher\s+(?:with\s+)?(?:named|called)?\s*(.+)/i,
    /(?:tell me about|status of|story for|profile of|info on|overview of|show(?:\s+me)?|know about|learn about)\s+(?:an?\s+)?teacher\s+(?:with\s+)?(.+)/i,
    /(?:who is|who's)\s+(?:teacher\s+)?(.+)/i,
    /(?:qui est)\s+(?:l['']?)?(?:enseignant(?:e)?\s+)?(.+)/i,
    /(?:fiche|profil|histoire)\s+(?:de l'?)?enseignant(?:e)?\s+(?:avec\s+)?(?:matricule|registration|reg)?\s*(?:[:\s#-]+)?(.+)/i,
    /teacher\s+(?:with\s+)?(?:registration\s*(?:no|number|#)?|reg(?:istration)?\s*(?:no|number|#)?|matricule)\s*[:\s#-]*(.+)/i,
    /teacher\s+(?:named|called)\s+(.+)/i,
    /enseignant(?:e)?\s+(?:nomme|nommé|nommee|nommée|appele|appelé|appelee|appelée)\s+(.+)/i,
    /(?:is|are)\s+teacher\s+([a-zA-ZÀ-ÿ][\wÀ-ÿ.-]*(?:\s+[a-zA-ZÀ-ÿ][\wÀ-ÿ.-]*)?)\s+(?:assigned|on leave|attending|present)/i,
    /(?:is|are)\s+([a-zA-ZÀ-ÿ][\wÀ-ÿ.-]*(?:\s+[a-zA-ZÀ-ÿ][\wÀ-ÿ.-]*)?)\s+(?:assigned|on leave|attending|present)/i,
    /(?:est-ce que|est ce que)\s+([a-zA-ZÀ-ÿ][\wÀ-ÿ.-]*(?:\s+[a-zA-ZÀ-ÿ][\wÀ-ÿ.-]*)?)\s+est\b/i,
  ];

  for (const re of patterns) {
    const match = raw.match(re);
    if (match?.[1]) {
      let term = stripTeacherQueryBoilerplate(match[1]);
      const nestedReg = parseRegistrationHint(term);
      if (nestedReg) return nestedReg;
      if (term) return term;
    }
  }

  let term = extractSearchTerm(input, language);
  term = stripTeacherQueryBoilerplate(term);
  term = term.replace(/\s+(assigned|on leave|attending|present|affectee|affectée|affecte|affecté).*$/i, "");
  term = term.replace(/\s+est\s+(affect|assign|conge).*$/i, "");
  return term.trim();
}

export function extractSearchTerm(input, language) {
  const raw = String(input ?? "").trim();
  const regHint = parseRegistrationHint(raw);
  if (regHint) return regHint;

  let term = stripTeacherQueryBoilerplate(raw);

  const prefixes =
    language === "en"
      ? [
          "search teacher",
          "find teacher",
          "look up teacher",
          "lookup teacher",
          "tell me about teacher",
          "show teacher",
          "who is teacher",
          "who is",
          "chercher enseignant",
          "trouver enseignant",
          "rechercher enseignant",
          "fiche enseignant",
          "teacher",
          "enseignant",
        ]
      : [
          "chercher enseignant",
          "trouver enseignant",
          "rechercher enseignant",
          "fiche enseignant",
          "profil enseignant",
          "qui est l'enseignant",
          "qui est enseignant",
          "qui est",
          "search teacher",
          "find teacher",
          "enseignant",
          "teacher",
        ];

  prefixes.forEach((p) => {
    const re = new RegExp(`^${p}\\s*:?\\s*`, "i");
    term = term.replace(re, "");
  });

  term = stripTeacherQueryBoilerplate(term);
  const nestedReg = parseRegistrationHint(term);
  if (nestedReg) return nestedReg;

  return term;
}
